import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";
import WhoWeServeFeatureSection from "@/components/who-we-serve/WhoWeServeFeatureSection";

export default function ForPersonalTrainersPage() {
  return (
    <>
      <Head>
        <title>
          Featcal for Personal Trainers | Consistency between sessions
        </title>
        <meta
          name="description"
          content="Featcal helps personal trainers keep clients consistent between sessions with structured tracking, workouts and plans in-app, and faster progress review-so results stick."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            For personal trainers
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            Get better consistency between sessions-without more admin.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            Featcal gives clients one app to track and follow plans, and gives
            you a clean review flow-so you can coach performance and adherence,
            not chase screenshots.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Start for free
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              See FAQs
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              The real challenge: consistency between sessions
            </h2>
            <ul className="mt-4 space-y-3 text-slate-700">
              {[
                "Workouts and habits don’t stick without daily structure.",
                "Updates show up as screenshots and DMs.",
                "You end up doing admin instead of coaching performance.",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="text-slate-400 font-bold">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Workouts delivered in-app",
                desc: "Give clients a clear plan to follow with less back-and-forth.",
              },
              {
                title: "Track what matters",
                desc: "Workouts, steps, sleep, hydration, meals-keep accountability simple and consistent.",
              },
              {
                title: "Faster progress review",
                desc: "Spot adherence gaps and trends quickly so you adjust the plan earlier.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Great fit for
              </h2>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Body recomposition programs (training + nutrition consistency).",
                  "Fat loss programs where adherence drives results.",
                  "Strength gain / weight gain programs with structured follow-up.",
                  "Hybrid coaching with check-ins + between-session accountability.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                What Featcal replaces
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Spreadsheets and scattered apps.",
                  "Workout resends and “what’s my plan?” messages.",
                  "Screenshots for progress updates.",
                  "Chasing adherence between sessions.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-slate-400 font-bold">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <WhoWeServeFeatureSection
          eyebrow="Workout plans"
          title="Plans clients actually follow"
          description="Deliver training in-app so clients always know what to do-and you can review adherence without chasing screenshots."
          bullets={[
            "Less admin and fewer resends.",
            "More consistency between sessions.",
            "Faster progress review to adjust earlier.",
          ]}
          imageSrc="/images/workout.webp"
          imageAlt="Workout plan preview"
          align="right"
          imageFit="contain"
        />

        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-blue-100 rounded-3xl p-8 md:p-10 shadow-xl shadow-blue-100/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                  Ready to improve adherence between sessions?
                </h2>
                <p className="text-slate-700 mt-2">
                  Start free and run your next week with less admin.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Start for free
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  See FAQs
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
